import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { faMinusCircle } from "@fortawesome/free-solid-svg-icons/faMinusCircle";
import { VerificationTransactionDetailsPolicyDecisionConditionResult } from "@idw-types/data/verification-transaction-details";
import { CSSProperties, useMemo } from "react";
import classNames from "classnames";
import styles from "./policy-decision-condition-result-diagram.less";

type PolicyDecisionConditionResultDiagramProps = {
    policyDecisionConditionResult: VerificationTransactionDetailsPolicyDecisionConditionResult;
    depth: number;
    index: number;
    siblingCount: number;
    parentPolicyDecisionConditionResult: VerificationTransactionDetailsPolicyDecisionConditionResult;
    andOrTextBackgroundColor: string;
};

export const PolicyDecisionConditionResultDiagram = (props: PolicyDecisionConditionResultDiagramProps) => {
    const { policyDecisionConditionResult, depth, index, siblingCount, parentPolicyDecisionConditionResult, andOrTextBackgroundColor } = props;

    const conditionResultIcon = useMemo(() => {
        switch (policyDecisionConditionResult.result) {
            case "pass":
                return (
                    <FontAwesomeIcon className={styles.passIcon} icon={faCheckCircle} />
                );
            case "fail":
                return (
                    <FontAwesomeIcon className={styles.failIcon} icon={faTimesCircle} />
                );
            default:
                return (
                    <FontAwesomeIcon className={styles.defaultIcon} icon={faMinusCircle} />
                );
        }
    }, [policyDecisionConditionResult.result]);

    const horizontalLineClassName = useMemo(() => {
        switch (policyDecisionConditionResult.result) {
            case "pass":
                return classNames(styles.horizontalLine, styles.greenLine);
            case "fail":
                return classNames(styles.horizontalLine, styles.redLine);
            default:
                return styles.horizontalLine;
        }
    }, [policyDecisionConditionResult.result]);

    const verticalLineClassName = useMemo(() => {
        switch (parentPolicyDecisionConditionResult?.result) {
            case "pass":
                return classNames(styles.verticalLine, styles.greenLine);
            case "fail":
                return classNames(styles.verticalLine, styles.redLine);
            default:
                return styles.verticalLine;
        }
    }, [policyDecisionConditionResult.result]);

    const getDescendantCount = (policyDecisionConditionResults: VerificationTransactionDetailsPolicyDecisionConditionResult) => {
        let count = 1;
        if (policyDecisionConditionResults.details && policyDecisionConditionResults.details.length > 0) {
            const childrenCounts: Array<number> = policyDecisionConditionResults.details.map(detail => getDescendantCount(detail));
            count += childrenCounts.reduce((prev: number, curr: number) => prev + curr, 0);
        }

        return count;
    };

    const andOrTextStyle = useMemo(() => {
        const style: CSSProperties = {};
        if (
            parentPolicyDecisionConditionResult?.details &&
            parentPolicyDecisionConditionResult?.details.length > 1 &&
            index !== 0
        ) {
            const numberOfDescendants = getDescendantCount(parentPolicyDecisionConditionResult.details[index - 1]);
            const topValue = 1.2 * numberOfDescendants - 0.6;
            style.top = `-${topValue}rem`;
        } else {
            style.top = "-0.8rem";
        }

        style.backgroundColor = andOrTextBackgroundColor;
        return style;
    }, [policyDecisionConditionResult.details]);


    const verticalLineStyle = useMemo(() => {
        const style: CSSProperties = {};

        if (index === 0) {
            style.top = "-0.9rem";
            style.bottom = "-0.9rem";

            if (index === siblingCount - 1) {
                style.height = "2.2rem";
            } else {
                style.height = "4.4rem";
            }
        } else if (
            parentPolicyDecisionConditionResult?.details &&
            parentPolicyDecisionConditionResult?.details.length > 1 &&
            index !== 0
        ) {
            const numberOfDescendants = getDescendantCount(parentPolicyDecisionConditionResult.details[index - 1]);
            let topValue = 3.6 * numberOfDescendants;
            let heightValue = 3.6 * numberOfDescendants;

            if (heightValue < 80) {
                topValue = topValue - (0.6 * (numberOfDescendants + 1));
            }

            if (index > 0) {
                topValue = topValue + 0.6;
                heightValue = heightValue + 0.6;
            }

            style.top = `-${topValue}rem`;
            style.bottom = `-0.9rem`;
            style.height = `${heightValue}rem`;

            if (index === siblingCount - 1 && numberOfDescendants > 1) {
                const lastItemHeightValue = heightValue - (0.6 * (numberOfDescendants - 1));
                style.height = `${lastItemHeightValue}rem`;
            }
        } else {
            style.top = "-0.9rem";
            style.bottom = "-0.9rem";
            style.height = "4.4rem";
        }

        return style;
    }, [policyDecisionConditionResult.details, index, siblingCount]);

    return (
        <div className={classNames(styles.policyDecisionConditionResultDiagram, depth > 0 ? styles.indented : "")}>
            <div className={styles.resultContainer}>
                <div className={styles.iconContainer}>{conditionResultIcon}</div>
                <div className={styles.nameContainer}>{policyDecisionConditionResult.name}</div>
                {depth > 0 && (
                    <>
                        <div className={verticalLineClassName} style={verticalLineStyle} />
                        <div className={horizontalLineClassName} />
                        {index > 0 && (
                            <div className={styles.andOrText} style={andOrTextStyle}>
                                {parentPolicyDecisionConditionResult?.condition === "Must Pass One or More" ? (
                                    <span>OR</span>
                                ) : (
                                    <span>AND</span>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            {policyDecisionConditionResult.details && policyDecisionConditionResult.details.length > 0 && (
                <>
                    {policyDecisionConditionResult.details.map((detail, index) => (
                        <PolicyDecisionConditionResultDiagram
                            key={`parent-${policyDecisionConditionResult.name}-child-${detail.name}-depth-${depth}`}
                            policyDecisionConditionResult={detail}
                            depth={depth + 1}
                            index={index}
                            siblingCount={policyDecisionConditionResult.details.length}
                            parentPolicyDecisionConditionResult={policyDecisionConditionResult}
                            andOrTextBackgroundColor={andOrTextBackgroundColor}
                        />
                    ))}
                    <div className={styles.verticalLine}></div>
                </>
            )}
        </div>
    );
};